import React, { lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AuthLayout from './components/layout/AuthLayout';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import { PREFIXED_LANGUAGES } from './i18n/config';
import LanguageRouteSync from './components/LanguageRouteSync';
import HtmlLangDir from './components/HtmlLangDir';

const Layout = lazy(() => import('./components/layout/Layout'));
const LandingPage = lazy(() => import('./pages/LandingPage'));
// Same factories as the preload map in index.js, so webpack emits one chunk per
// route and the preloaded module is already cached when lazy() resolves.
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'));
const Terms = lazy(() => import('./pages/Terms/Terms'));
const Support = lazy(() => import('./pages/Support/Support'));
const PricingPopup = lazy(() => import('./components/PricingPopup'));

// Global pricing popup — shows on whichever page the user is on.

// Lazy-loaded pages
const FieldJobs = lazy(() => import('./pages/fieldjobs/FieldJobs'));
const Login = lazy(() => import('./pages/auth/Login'));
const ForgotPassword = lazy(() => import('./pages/auth/ForgotPassword'));
const OrganizationSignup = lazy(() => import('./pages/auth/OrganizationSignup'));

const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));

const JobCardList = lazy(() => import('./pages/jobcards/JobCardList'));
const JobCardDetail = lazy(() => import('./pages/jobcards/JobCardDetail'));
const JobCardPrint = lazy(() => import('./pages/jobcards/JobCardPrint'));
const JobCardCreationForm = lazy(() => import('./pages/jobcards/JobCardCreationForm'));
const CreateCustomerForm = lazy(() => import('./pages/jobcards/CreateCustomerForm'));
const CustomerList = lazy(() => import('./pages/jobcards/CustomerList'));
const VehicleDetailsForm = lazy(() => import('./pages/jobcards/VehicleDetailsForm'));
const VehicleList = lazy(() => import('./pages/jobcards/VehicleList'));

const OrganizationSettings = lazy(() => import('./pages/organization/OrganizationSettings'));
const UserList = lazy(() => import('./pages/users/UserList'));
const UserProfile = lazy(() => import('./pages/users/UserProfile'));
const RoleManagement = lazy(() => import('./pages/roles/RoleManagement'));

const InquiriesList = lazy(() => import('./pages/Crm/InquiriesList'));
const InquiryForm = lazy(() => import('./pages/Crm/InquiriesFrom'));
const InquireDetail = lazy(() => import('./pages/Crm/InquireDetail'));

const BayFrom = lazy(() => import('./pages/Bay/BayFrom'));
const BaysList = lazy(() => import('./pages/Bay/BayList'));

const Services = lazy(() => import('./components/Services'));
const Booking = lazy(() => import('./pages/booking/Booking'));

const GarageMail = lazy(() => import('./pages/mail/GarageMail'));

// Platform billing — this garage's own plan and payments to JobCard. Not the
// invoices it issues to its customers, which live under /Invoices.
const Billing = lazy(() => import('./pages/billing/Billing'));
const CheckoutReturn = lazy(() => import('./pages/billing/CheckoutReturn'));
// Public on purpose: Stripe returns mobile payers here in the device browser,
// where they are not signed in to the web app. See the file header.
const CheckoutThanks = lazy(() => import('./pages/billing/CheckoutThanks'));

const NotFound = lazy(() => import('./pages/NotFound'));
const Unauthorized = lazy(() => import('./pages/Unauthorized'));

const TechnicianLogin = lazy(() => import('./pages/technician/TechnicianLogin'));
const TechnicianSignup = lazy(() => import('./pages/technician/TechnicianSignup'));
const TechnicianDashboard = lazy(() => import('./pages/technician/TechnicianDashboard'));
const TechnicianMarketplace = lazy(() => import('./pages/technician/TechnicianMarketplace'));

const Loading = () => (
  <div className="min-h-screen flex items-center justify-center bg-jc-bg">
    <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-500 border-t-transparent"></div>
  </div>
);

// Every operational role except the platform-level ones (superadmin) and the
// marketplace identity (technician_marketplace) — the "All Authenticated
// Users" tier used throughout the route table below.
const ALL_ROLES = ['admin', 'supervisor', 'technician', 'user', 'accountant'];

// Literally every User.role enum value except 'custom' — used only for
// /dashboard, which historically had NO role restriction at all (any
// authenticated account, including 'customer'/'superadmin'/
// 'technician_marketplace'/'field_supervisor', which ALL_ROLES above
// deliberately excludes). Adding a permission gate for the new 'custom' role
// must not narrow who could already reach it.
const NON_CUSTOM_ROLES = [
  'admin', 'technician', 'supervisor', 'customer', 'user',
  'technician_marketplace', 'superadmin', 'field_supervisor', 'accountant',
];

function App() {
  return (
    <ErrorBoundary>
      <HelmetProvider>
          {/* The token scope lives on <html>, applied before paint by the inline
              script in index.html. Rendering it here instead would make React's
              output depend on localStorage, which the prerendered HTML cannot
              know about — that is a hydration mismatch on every dark-theme visit.

              MUI's ThemeProvider is deliberately NOT here — it lives in
              MuiProvider, mounted inside Layout, so MUI stays out of the main
              bundle. See that file for why. */}
          <div className="min-h-screen bg-jc-bg">
          {/* Was never mounted: all 7 toast() call sites were no-ops. */}
          <ToastContainer position="top-right" autoClose={4000} newestOnTop theme="dark" />
          {/* Keeps the active language following the URL across client-side
              navigations; the detector only runs once, at init. */}
          <LanguageRouteSync />
          {/* Declares <html lang/dir> on EVERY route. It has to be mounted
              unconditionally: Helmet removes any html attribute that no
              mounted Helmet still declares, so leaving this to <SEO> — which
              only nine pages render — stripped `dir` off the document the
              moment an Arabic reader opened a page without it. */}
          <HtmlLangDir />
          <Suspense fallback={<Loading />}>
            <Routes>
              {/* Public Landing Page */}
              <Route path="/" element={<LandingPage />} />

              {/*
                The same three public pages, addressable per language.

                A search engine indexes URLs, not localStorage — with one URL
                serving all three languages only the crawler's language could
                ever be indexed, and hreflang would have nothing to point at.
                English stays unprefixed: it is the x-default and '/' already
                carries whatever ranking the site has.

                Only these three are duplicated. The authenticated app is
                noindex behind a login, so prefixing it would double every
                route to no benefit.
              */}
              {PREFIXED_LANGUAGES.map((lang) => (
                <Route key={lang} path={`/${lang}`}>
                  <Route index element={<LandingPage />} />
                  <Route path="PrivacyPolicy" element={<PrivacyPolicy />} />
                  <Route path="terms" element={<Terms />} />
                  <Route path="support" element={<Support />} />
                </Route>
              ))}

              {/* Auth Routes */}
              <Route element={<AuthLayout />}>
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/organization-signup" element={<OrganizationSignup />} />
              </Route>

              {/* Protected Routes - No Role Required */}
              <Route element={<PrivateRoute />}>
                <Route element={<Layout />}>
                  <Route path="/users/profile" element={<UserProfile />} />
                  <Route path="/unauthorized" element={<Unauthorized />} />
                </Route>
              </Route>

              {/* server/routes/jobcards.js's GET /Dashboard/:id requires
                  dashboard:view for role: 'custom' — this stays in step with
                  it, so an employee whose role admin left Dashboard unchecked
                  gets the normal /unauthorized redirect instead of landing
                  (Login.js's default post-login destination) on a page whose
                  data call silently 403s. */}
              <Route element={<PrivateRoute requiredRoles={NON_CUSTOM_ROLES} requiredPermission="dashboard:view" />}>
                <Route element={<Layout />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                </Route>
              </Route>

              {/*
                Protected Routes - Admin Only. Deliberately no `requiredPermission`
                escape hatch: a custom role (see /roles) can never be granted
                access to managing employees, roles, or platform billing.
              */}
              <Route element={<PrivateRoute requiredRoles={['admin']} />}>
                <Route element={<Layout />}>
                  <Route path="/users" element={<UserList />} />
                  <Route path="/roles" element={<RoleManagement />} />
                  <Route path="/Services" element={<Services />} />
                  <Route path="/billing" element={<Billing />} />
                </Route>
              </Route>

              {/*
                Every route below pairs a fixed-role check with an equivalent
                `requiredPermission` for role: 'custom' — evaluated as one OR
                inside a single PrivateRoute (see PrivateRoute.js), never as
                two separate <Route> declarations for the same path. React
                Router resolves two <Route>s that declare the identical path
                by JSX order, not by which one's guard would pass — declaring
                a path twice previously left the second declaration's grant
                permanently unreachable (or, worse, shadowing the first for
                every role that isn't 'custom'). One declaration per path,
                every acceptable condition folded into its own guard.
              */}
              <Route element={<PrivateRoute requiredRoles={['admin']} requiredPermission="organization:view" />}>
                <Route element={<Layout />}>
                  <Route path="/organization" element={<OrganizationSettings />} />
                </Route>
              </Route>

              {/* Checkout return — outside Layout so the confirmation is the
                  whole screen, and admin-only to match who can start a purchase. */}
              <Route element={<PrivateRoute requiredRoles={['admin']} />}>
                <Route path="/billing/return" element={<CheckoutReturn />} />
              </Route>

              {/* Protected Routes - Superadmin Only (garage mail tool) */}
              <Route element={<PrivateRoute requiredRoles={['superadmin']} />}>
                <Route element={<Layout />}>
                  <Route path="/garage-mail" element={<GarageMail />} />
                </Route>
              </Route>

              <Route element={<PrivateRoute requiredRoles={['admin', 'supervisor']} requiredPermission="jobcards:edit" />}>
                <Route element={<Layout />}>
                  <Route path="/jobcards/createJobcard/:id" element={<JobCardCreationForm />} />
                </Route>
              </Route>
              {/* An accountant (see jobcard_report_accountent) also reads
                  customers and vehicles here; the books themselves live in
                  the accounts app, reached from the sidebar. */}
              <Route element={<PrivateRoute requiredRoles={['admin', 'supervisor', 'accountant']} requiredPermission="customers:view" />}>
                <Route element={<Layout />}>
                  <Route path="/customers" element={<CustomerList />} />
                </Route>
              </Route>
              <Route element={<PrivateRoute requiredRoles={['admin', 'supervisor']} requiredPermission="customers:edit" />}>
                <Route element={<Layout />}>
                  <Route path="/create-customer" element={<CreateCustomerForm />} />
                  <Route path="/jobcards/customer/:id" element={<CreateCustomerForm />} />
                </Route>
              </Route>
              <Route element={<PrivateRoute requiredRoles={['admin', 'supervisor']} requiredPermission="vehicles:edit" />}>
                <Route element={<Layout />}>
                  <Route path="/jobcards/vehicle/:id" element={<VehicleDetailsForm />} />
                  <Route path="/jobcards/updatedVehicle/:id" element={<VehicleDetailsForm />} />
                </Route>
              </Route>
              <Route element={<PrivateRoute requiredRoles={['admin', 'supervisor']} requiredPermission="jobcards:view" />}>
                <Route element={<Layout />}>
                  <Route path="/jobcards/Print/:id" element={<JobCardPrint />} />
                </Route>
              </Route>
              <Route element={<PrivateRoute requiredRoles={['admin', 'supervisor', 'accountant']} requiredPermission="vehicles:view" />}>
                <Route element={<Layout />}>
                  <Route path="/vehicles" element={<VehicleList />} />
                </Route>
              </Route>
              {/* Work routed in by a platform field supervisor: accept,
                  decline, or pass to another garage. */}
              <Route element={<PrivateRoute requiredRoles={['admin', 'supervisor']} requiredPermission="fieldJobs:view" />}>
                <Route element={<Layout />}>
                  <Route path="/field-jobs" element={<FieldJobs />} />
                </Route>
              </Route>
              <Route element={<PrivateRoute requiredRoles={['admin', 'supervisor']} requiredPermission="technicians:view" />}>
                <Route element={<Layout />}>
                  <Route path="/technicians" element={<TechnicianMarketplace />} />
                </Route>
              </Route>

              {/* Protected Routes - All Authenticated Users with any role (or
                  the matching custom permission) */}
              <Route element={<PrivateRoute requiredRoles={ALL_ROLES} requiredPermission="jobcards:view" />}>
                <Route element={<Layout />}>
                  <Route path='/jobCardDetail/:id' element={<JobCardDetail />} />
                  <Route path="/job-cards/:status/:id" element={<JobCardList />} />
                  <Route path="/job-cards/:id/print" element={<JobCardPrint />} />
                </Route>
              </Route>
              {/* CRM, Price Quotes and Invoices are the same underlying
                  Inquiry records and the same /api/inquiries routes,
                  distinguished only by the priceQuote/invoice flags each page
                  passes — see server/routes/inquiries.js's requireInquiryPermission. */}
              <Route element={<PrivateRoute requiredRoles={ALL_ROLES} requiredPermission="crm:view" />}>
                <Route element={<Layout />}>
                  <Route path="/inquiries" element={<InquiriesList payload={{ priceQuote: true }} />} />
                </Route>
              </Route>
              {/* The create/detail form is shared by all three categories
                  (:title picks which), so any one of the three view
                  permissions is enough — a priceQuotes-only custom role must
                  still be able to open it to create a price quote, even
                  though it holds no crm permission at all. */}
              <Route element={<PrivateRoute requiredRoles={ALL_ROLES} requiredPermission={['crm:view', 'priceQuotes:view', 'invoices:view']} />}>
                <Route element={<Layout />}>
                  <Route path="/InquiryForm/:title" element={<InquiryForm />} />
                  <Route path='/InquireDetail/:title' element={<InquireDetail />} />
                </Route>
              </Route>
              <Route element={<PrivateRoute requiredRoles={ALL_ROLES} requiredPermission="vehicles:view" />}>
                <Route element={<Layout />}>
                  <Route path="/vehicle/:id" element={<VehicleList />} />
                </Route>
              </Route>
              <Route element={<PrivateRoute requiredRoles={ALL_ROLES} requiredPermission="booking:view" />}>
                <Route element={<Layout />}>
                  <Route path="/Booking" element={<Booking />} />
                </Route>
              </Route>
              {/*
                `title` stays English on purpose: it is put straight into
                the URL (/InquiryForm/:title), so translating it would
                produce a route that does not resolve. `titleKey` is what
                the heading is rendered from.
              */}
              {/* i18n-ignore — see the note above: `title` is the URL value. */}
              <Route element={<PrivateRoute requiredRoles={ALL_ROLES} requiredPermission="priceQuotes:view" />}>
                <Route element={<Layout />}>
                  <Route path='/price-quotes' element={<InquiriesList title="Price Quotes" titleKey="menu.priceQuote" priceQuote={'priceQuote=true'} payload={{ priceQuote: true }} />} />
                </Route>
              </Route>
              {/* i18n-ignore */}
              <Route element={<PrivateRoute requiredRoles={ALL_ROLES} requiredPermission="invoices:view" />}>
                <Route element={<Layout />}>
                  <Route path='/Invoices' element={<InquiriesList title="Invoices" titleKey="menu.invoices" invoice={'invoice=true'} payload={{ invoice: true }} />} />
                </Route>
              </Route>
              <Route element={<PrivateRoute requiredRoles={ALL_ROLES} requiredPermission="bays:view" />}>
                <Route element={<Layout />}>
                  <Route path="/bays" element={<BaysList />} />
                </Route>
              </Route>
              <Route element={<PrivateRoute requiredRoles={ALL_ROLES} requiredPermission="bays:edit" />}>
                <Route element={<Layout />}>
                  <Route path="/bays/create" element={<BayFrom />} />
                </Route>
              </Route>

              {/* Technician marketplace — public auth pages */}
              <Route path="/technician/login" element={<TechnicianLogin />} />
              <Route path="/technician/signup" element={<TechnicianSignup />} />
              <Route path="/technician/dashboard" element={<TechnicianDashboard />} />

              {/* Public — the mobile checkout return. Must not be guarded. */}
              <Route path="/billing/thanks" element={<CheckoutThanks />} />

              {/* Public */}
              <Route path='/PrivacyPolicy' element={<PrivacyPolicy />} />
              <Route path='/terms' element={<Terms />} />
              <Route path='/support' element={<Support />} />
              {/* 404 Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          {/* Outside the route Suspense so a chunk load never unmounts it */}
          <Suspense fallback={null}>
            <PricingPopup />
          </Suspense>
          </div>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
